import React from "react";
import {
  BadgeAlert,
  Eye,
  MapPin,
  RefreshCw,
  Settings,
  Share2,
  Star,
} from "lucide-react";
import { useLanguage } from "../../../context/LanguageContext";
import { timeAgo } from "../../../helpers/function.helper";
import { getGenderPlaceholder } from "../../../helpers/matches.helper";
import { UPLOAD_URL } from "../../../utils/constants";
import { MatchListEntry } from "../../../types";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../../../utils/imagePrivacy";

const ProfileListItem = ({
  match,
  onViewProfile,
  onAddToShortlist,
  matches,
  setProfiles,
  isOtherPage,
  onSwitchProfile,
  onManageProfile,
  onReportProfile,
  onShareProfile,
}: {
  match: MatchListEntry;
  onViewProfile: (match: MatchListEntry) => void;
  onAddToShortlist?: (
    match: MatchListEntry,
    matches: MatchListEntry[],
    setProfiles: React.Dispatch<React.SetStateAction<MatchListEntry[]>>,
  ) => void;
  matches: MatchListEntry[];
  setProfiles: React.Dispatch<React.SetStateAction<MatchListEntry[]>>;
  isOtherPage?: boolean;
  onSwitchProfile?: (match: MatchListEntry) => void;
  onManageProfile?: (match: MatchListEntry) => void;
  onReportProfile?: (match: MatchListEntry) => void;
  onShareProfile?: (match: MatchListEntry) => void;
}) => {
  const { language, t } = useLanguage();
  const scoreLabel = t("matches.labels.score");
  const publicLabel = t("matches.labels.public");
  const viewsTemplate = t("matches.labels.viewsCount");
  const lastSeenPrefix = t("matches.labels.lastSeen");
  const createdByLabel = t("matches.labels.createdBy");
  const viewProfileLabel = t("matches.buttons.viewProfile");
  const switchLabel = t("matches.buttons.switch");
  const manageProfileLabel = t("matches.buttons.manageProfile");
  const manageLabel = t("matches.buttons.manage");
  const shareLabel = t("matches.buttons.share");
  const reportLabel = t("matches.buttons.report");

  const lastSeenLabel = `${lastSeenPrefix} ${timeAgo(
    match.lastSeenAt,
    language,
  )}`;
  const viewsLabel = viewsTemplate.replace("{count}", String(match.views ?? 0));
  const heightDisplay = match.height;
  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onViewProfile?.(match);
    }
  };

  const isPrivate = match.visibleToAll === "false";
  const blurPhoto = shouldBlurProfileImage(match.imagePrivacy);
  return (
    <div
      key={match.maritalId}
      className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
    >
      <div className="flex flex-col sm:hidden">
        <div className="mb-2 flex items-center gap-2 px-4 pt-3">
          <span className="rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold text-blue-600">
            {scoreLabel}: {match.profileScore}%
          </span>

          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full ${
              isPrivate ? "bg-gray-200" : "bg-green-100"
            }`}
          >
            <div
              className={`w-2 h-2 rounded-full ${
                isPrivate ? "bg-gray-500" : "bg-green-600"
              }`}
            />
            <span
              className={`text-[11px] font-semibold ${
                isPrivate ? "text-gray-700" : "text-green-700"
              }`}
            >
              {isPrivate ? "Private" : publicLabel}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-green-600">
              <Eye size={14} />
              <span className="text-[11px] font-semibold">{viewsLabel}</span>
            </div>
          </div>
        </div>
        <hr className="py-1 text-gray-700" />
        <div className="flex items-start px-5">
          <div>
            <h3 className="text-sm font-semibold leading-tight text-gray-900 truncate">
              {match.fullName}
            </h3>
            <p className="mt-1 flex flex-wrap text-[11px] text-gray-500 truncate">
              {match.maritalId} | {lastSeenLabel}
            </p>
          </div>
        </div>
      </div>

      <div
        className="relative flex flex-row items-start gap-3 sm:gap-4 p-3 sm:p-4 cursor-pointer transition hover:bg-purple-50/40 focus:outline-none"
        onClick={() => onViewProfile?.(match)}
        role="button"
        tabIndex={0}
        onKeyDown={handleCardKeyDown}
      >
        {/* LEFT IMAGE */}
        <div
          className="
                 relative
                    h-40 w-32            /* mobile */
                    sm:h-48 sm:w-32      /* small screens */
                    md:h-48 md:w-48      /* medium screens */
                    lg:h-64 lg:w-64      /* large screens */
                    xl:h-72 xl:w-72      /* extra-large screens */
                    rounded-xl
                    overflow-hidden
                    flex-shrink-0
              "
        >
          <img
            src={UPLOAD_URL + "/users/profile_pic/" + match.image}
            alt={match.fullName}
            className={`w-full h-full object-cover object-top ${
              blurPhoto ? "blur-sm scale-110 brightness-90" : ""
            }`}
            onError={(event) => {
              (event.currentTarget as HTMLImageElement).src =
                getGenderPlaceholder(match.gender);
            }}
          />
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onAddToShortlist?.(match, matches, setProfiles);
            }}
            aria-label="Shortlist profile"
            className="absolute top-2 left-2 flex items-center justify-center rounded-full bg-white/90 p-1.5 shadow-md text-orange-500 hover:bg-white"
          >
            {match.isShortlisted ? (
              <Star className="w-4 h-4" fill="currentColor" />
            ) : (
              <Star className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* RIGHT DETAILS */}
        <div className="flex-1 min-w-0 flex flex-col relative w-full">
          {/* TOP ROW: name + more icon */}
          <div className="hidden sm:flex items-center gap-4 mb-3">
            <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
              {scoreLabel}: {match.profileScore}%
            </span>
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${
                isPrivate ? "bg-gray-200" : "bg-green-100"
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  isPrivate ? "bg-gray-500" : "bg-green-600"
                }`}
              />
              <span
                className={`text-xs font-semibold ${
                  isPrivate ? "text-gray-700" : "text-green-700"
                }`}
              >
                {isPrivate ? "Private" : publicLabel}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-green-600">
                <Eye size={16} />
                <span className="text-sm font-semibold">{viewsLabel}</span>
              </div>
            </div>
          </div>

          <div className="items-start hidden sm:flex">
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-gray-900 leading-tight truncate">
                {match.fullName}
              </h3>
              <p className="flex flex-wrap text-xs text-gray-500 truncate mt-2">
                {match.maritalId} | <span> {lastSeenLabel}</span>
              </p>
            </div>
          </div>

          {/* DETAILS BELOW */}
          <div className="mt-1 mb-4 space-y-1 text-[11px] leading-snug text-gray-600 sm:mt-2 sm:mb-5 sm:text-xs sm:leading-relaxed">
            <p className="flex flex-wrap gap-x-2 gap-y-1">
              {match.maritalStatus && <span>{match.maritalStatus}</span>}
              <span>
                {createdByLabel}: {match.createdBy}
              </span>
            </p>
            <p className="flex flex-wrap gap-x-2 gap-y-1">
              <span>{match.age}</span>
              {heightDisplay && <span>{heightDisplay}</span>}
              {match.caste && <span>{match.caste}</span>}
            </p>
            <p className="flex flex-wrap gap-x-2 gap-y-1">
              {match.education && <span>{match.education}</span>}
              {match.occupation && <span>{match.occupation}</span>}
              {match.annualIncome && <span>{match.annualIncome}</span>}
            </p>
            {[match.location, match.district, match.state, match.country].some(
              Boolean,
            ) && (
              <p className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-purple-500" />
                <span className="truncate">
                  {[match.location, match.district, match.state, match.country]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </p>
            )}
          </div>

          {/* DESKTOP BUTTONS */}
          <div className="hidden sm:flex flex-wrap gap-2 justify-start pt-2">
            <button
              className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
              onClick={(event) => {
                event.stopPropagation();
                onViewProfile(match);
              }}
            >
              <Eye size={18} />
              <span className="hidden sm:flex">{viewProfileLabel}</span>
            </button>
            {!isOtherPage && (
              <>
                <div className="relative">
                  <button
                    className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSwitchProfile ? onSwitchProfile(match) : null;
                    }}
                  >
                    <RefreshCw size={14} />
                    <span className="hidden sm:flex">{switchLabel}</span>
                  </button>

                  {match?.switchCount ? (
                    <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-red-500 text-[10px] font-semibold text-white">
                      {match?.switchCount}
                    </span>
                  ) : null}
                </div>

                <button
                  className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
                  onClick={(event) => {
                    event.stopPropagation();
                    onManageProfile ? onManageProfile(match) : null;
                  }}
                >
                  <Settings size={14} />
                  <span className="hidden sm:flex">{manageProfileLabel}</span>
                </button>
              </>
            )}
            <button
              className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
              onClick={(event) => {
                event.stopPropagation();
                onShareProfile?.(match);
              }}
            >
              <Share2 size={20} />
              <span className="hidden sm:flex">{shareLabel}</span>
            </button>
            {/* {isOtherPage && (
              <button
                className="px-4 py-2 border border-gray-300 bg-red-400 rounded-lg flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm hover:bg-red-500 text-white transition"
                onClick={(event) => {
                  event.stopPropagation();
                  onReportProfile?.(match);
                }}
              >
                <BadgeAlert size={14} />
                <span className="hidden sm:flex">{reportLabel}</span>
              </button>
            )} */}
          </div>
        </div>
      </div>

      {/* MOBILE BOTTOM BAR */}
            {isOtherPage ? (
        <div className="border-t border-gray-100 md:hidden">
          {/* <div className="grid grid-cols-2 divide-x"> */}
          <div className="flex items-center gap-2 justify-center px-4">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onShareProfile?.(match);
              }}
              className="flex items-center justify-center gap-2 py-3 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition"
            >
              <Share2 className="w-4 h-4" />
              <span>{shareLabel}</span>
            </button>
            {/* <button
              className="px-4 py-2 border border-gray-300 bg-red-400 rounded-lg flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm hover:bg-red-500 text-white transition"
              onClick={(event) => {
                event.stopPropagation();
                onReportProfile?.(match);
              }}
            >
              <BadgeAlert className="w-4 h-4" />
              <span>{reportLabel}</span>
            </button> */}
          </div>
        </div>
            ) : (
              <div className="border-t border-gray-100 md:hidden">
                <div className="grid grid-cols-3 gap-2 p-2">
                  <div className="relative">
                    <button
                      type="button"
                      className={`flex w-full min-w-0 items-center justify-center gap-1 rounded-xl px-2 py-3 text-[11px] font-semibold transition ${"text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSwitchProfile?.(match);
                      }}
                    >
                      <RefreshCw className="h-4 w-4 shrink-0" />
                      <span className="truncate">{switchLabel}</span>
                      {match?.switchCount ? (
                        <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-red-500 text-[10px] font-semibold text-white">
                          {match?.switchCount}
                  </span>
                ) : null}
              </button>
            </div>

                  <button
                    type="button"
                    className={`flex w-full min-w-0 items-center justify-center gap-1 rounded-xl px-2 py-3 text-[11px] font-semibold transition ${"text-white bg-gradient-to-r from-orange-400 to-pink-500 hover:from-orange-500 hover:to-pink-600"}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onManageProfile ? onManageProfile(match) : null;
                    }}
                  >
                    <Settings className="h-4 w-4 shrink-0" fill={"none"} />
                    <span className="truncate">{manageLabel}</span>
                  </button>
                  {/* 🔗 Share */}
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onShareProfile?.(match);
                    }}
                    className="flex w-full min-w-0 items-center justify-center gap-1 rounded-xl px-2 py-3 text-[11px] font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition"
                  >
                    <Share2 className="h-4 w-4 shrink-0" />
                    <span className="truncate">{shareLabel}</span>
                  </button>
                </div>
              </div>
      )}
    </div>
  );
};

export default ProfileListItem;
